import { Box, Stack } from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import {
  CHATAI_LOADING_STATUS,
  Message,
  MessagesContentStack,
  TypingIndicator,
} from '@ltvco/refresh-lib/v2';
import { useRef, useEffect, useCallback, useState } from 'react';

import { MessagePair } from '../../components/MessagePair';
import { RetrySearchInstruction } from '../../components/RetrySearchInstruction';
import { useScrollManagement } from '../../hooks/useScrollManagement';
import { useStickyHeader } from '../../hooks/useStickyHeader';
import { useTailGap } from '../../hooks/useTailGap';
import { useMessagePairing } from '../hooks/useMessagePairing';
import { extractMarketValueFromMessage } from '../utils';

import { ValueForecasterContent } from './ValueForecasterContent';

export interface LoadingStatus {
  message: string;
  subtitle: string;
}

type MessagesContainerProps = {
  messages: Message[];
  loadingStatus: LoadingStatus;
  onSendMessage?: (message?: string, imageBase64?: string) => void;
  onNewForecast: () => void;
  shouldResetInputs: boolean;
  onResetComplete: () => void;
};

export const MessagesContainer = ({
  messages,
  loadingStatus,
  onSendMessage,
  onNewForecast,
  shouldResetInputs,
  onResetComplete,
}: MessagesContainerProps) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { isMobile } = useScreenSize();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const handleProcessingStateChange = useCallback((processing: boolean) => {
    setIsProcessing(processing);
  }, []);

  useEffect(() => {
    if (messages.length === 0) {
      setIsProcessing(false);
    }
  }, [messages.length]);

  // Refs for layout/scroll
  const scrollRootRef = useRef<HTMLDivElement | null>(null);
  const messageRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const gapRef = useRef<HTMLDivElement | null>(null);
  const typingRef = useRef<HTMLDivElement | null>(null);

  // Use custom hooks for complex logic
  const {
    pairs,
    firstValueForecastAssistantIndex,
    firstValueForecastData,
    hasError,
  } = useMessagePairing(messages);

  useEffect(() => {
    if (
      isProcessing &&
      loadingStatus.message === CHATAI_LOADING_STATUS.IDLE.message &&
      (firstValueForecastData || hasError)
    ) {
      setIsProcessing(false);
    }
  }, [isProcessing, loadingStatus.message, firstValueForecastData, hasError]);

  const { hasUserScrolled } = useScrollManagement({
    scrollRootRef,
    messageRefs,
    pairs,
    messagesLength: messages.length,
    loadingMessage: loadingStatus.message,
    featureTitle: 'Value Forecaster',
  });

  const stickyIndex = useStickyHeader({
    scrollRootRef,
    messageRefs,
    pairs,
    messages,
  });

  const tailGap = useTailGap({
    scrollRootRef,
    messageRefs,
    gapRef,
    typingRef,
    pairs,
    loadingMessage: loadingStatus.message,
    hasError,
    maxTailGap: 80,
    featureTitle: 'Value Forecaster',
  });

  const firstUserMessage = messages.find((msg) => msg.role === 'user');
  const marketValue = firstUserMessage
    ? extractMarketValueFromMessage(firstUserMessage)
    : 0;
  const isContextLoading =
    loadingStatus.message !== CHATAI_LOADING_STATUS.IDLE.message;
  const isLoading = isProcessing || isContextLoading;

  // Cleanup timeout on component unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <MessagesContentStack
      ref={scrollRootRef}
      width="100%"
      spacing={2}
      paddingBottom={isMobile ? '130px' : '20px'}
      height={isMobile ? '100%' : 'auto'}
      minHeight={isMobile ? 0 : 'auto'}
      flexGrow={1}
      sx={{
        overflowY: isMobile && isTooltipOpen ? 'hidden' : 'auto',
      }}
    >
      <ValueForecasterContent
        onSendMessage={onSendMessage}
        firstValueForecastData={firstValueForecastData}
        marketValue={marketValue}
        shouldResetInputs={shouldResetInputs}
        onResetComplete={onResetComplete}
        onProcessingStateChange={handleProcessingStateChange}
        hasError={hasError}
        onNewForecast={onNewForecast}
        isLoading={isLoading}
        onTooltipOpenChange={setIsTooltipOpen}
      />
      {isProcessing && pairs.length === 0 && (
        <Box ref={typingRef} sx={{ width: '100%', maxWidth: '740px' }}>
          <TypingIndicator loadingStatus={loadingStatus} />
        </Box>
      )}
      {pairs.map((pair, index) => (
        <MessagePair
          key={pair.id}
          userMessage={pair.user}
          assistantMessage={pair.assistant}
          userIndex={pair.userIndex}
          assistantIndex={pair.assistantIndex}
          isLastPair={index === pairs.length - 1}
          isSticky={stickyIndex === pair.userIndex}
          hasUserScrolled={hasUserScrolled}
          loadingStatus={loadingStatus}
          firstEstimateAssistantIndex={firstValueForecastAssistantIndex}
          tailGap={tailGap}
          messageRefs={messageRefs}
          typingRef={typingRef}
          gapRef={gapRef}
        />
      ))}
      {loadingStatus.message === CHATAI_LOADING_STATUS.IDLE.message &&
        hasError && (
          <Stack width="100%" alignItems="center">
            <RetrySearchInstruction onRetrySearch={onNewForecast} />
          </Stack>
        )}
    </MessagesContentStack>
  );
};
